import { scaleLinear } from '@visx/scale';
import { useMemo, useState, type KeyboardEvent } from 'react';
import { histogramBins, placeLabels } from './math';
import { useElementSize } from './use-element-size';

export interface HistogramMarker {
  value: number;
  label: string;
}

export interface HistogramProps {
  values: ArrayLike<number> & Iterable<number>;
  height: number;
  color: string;
  formatX: (value: number) => string;
  /** Tooltip body for a bin: its range and the share of values in it. */
  formatBin: (from: number, to: number, share: number) => string;
  ariaLabel: string;
  bins?: number;
  /** Vertical reference lines (e.g. P10, median, P90). */
  markers?: readonly HistogramMarker[];
  /** Fixed width; measured from the container when omitted. Required with `print`. */
  width?: number;
  /** Static output for print and export: a bare `<svg>`, no hover or tooltip (see `ChartFrame`). */
  print?: boolean;
  /** Label size; 11 on screen, 7 in print. */
  fontSize?: number;
}

const BASE_MARGIN = { top: 16, right: 12, bottom: 24, left: 12 };

/**
 * Distribution of values (e.g. net worth across Monte Carlo paths at a date). Bars share the
 * surface-gap convention of the other charts; hover or ←/→ shows a bin's range and share.
 */
export function Histogram({
  values,
  height,
  color,
  formatX,
  formatBin,
  ariaLabel,
  bins = 30,
  markers = [],
  width: fixedWidth,
  print = false,
  fontSize = print ? 7 : 11,
}: HistogramProps) {
  const [ref, measured] = useElementSize<HTMLDivElement>();
  const width = fixedWidth ?? measured.width;
  const [hoverState, setHover] = useState(-1);
  const hover = print ? -1 : hoverState;
  const MARGIN = { ...BASE_MARGIN, top: fontSize + 5, bottom: fontSize + 13 };
  const data = useMemo(() => histogramBins(values, bins), [values, bins]);
  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, height - MARGIN.top - MARGIN.bottom);
  const first = data[0];
  const last = data.at(-1);
  const x = scaleLinear<number>({
    domain: [
      first?.from ?? 0,
      last && last.to > (first?.from ?? 0) ? last.to : (first?.from ?? 0) + 1,
    ],
    range: [0, innerWidth],
  });
  const maxCount = Math.max(1, ...data.map((b) => b.count));
  const y = scaleLinear<number>({ domain: [0, maxCount], range: [innerHeight, 0] });
  const barWidth = data.length > 0 ? innerWidth / data.length : 0;
  const hovered = data[hover];

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (data.length === 0) return;
    if (event.key === 'ArrowRight') setHover((h) => Math.min(data.length - 1, h + 1));
    else if (event.key === 'ArrowLeft') setHover((h) => Math.max(0, h < 0 ? 0 : h - 1));
    else return;
    event.preventDefault();
  };

  const svg = width > 0 && (
    <svg width={width} height={height} role="img" aria-label={ariaLabel}>
      <g transform={`translate(${String(MARGIN.left)},${String(MARGIN.top)})`}>
        {data.map((bin, i) => {
          const h = innerHeight - y(bin.count);
          return (
            <rect
              key={i}
              x={i * barWidth + 1}
              y={innerHeight - h}
              width={Math.max(0, barWidth - 2)}
              height={h}
              rx={Math.min(2, barWidth / 4)}
              style={{ fill: color, opacity: hover < 0 || hover === i ? 1 : 0.45 }}
            />
          );
        })}
        <line
          aria-hidden
          y1={innerHeight}
          y2={innerHeight}
          x2={innerWidth}
          style={{ stroke: 'var(--chart-axis)' }}
          strokeWidth={print ? 0.5 : 1}
          shapeRendering="crispEdges"
        />
        {placeLabels(markers.map((m) => x(m.value))).map((showLabel, i) => {
          const m = markers[i];
          if (!m) return null;
          return (
            <g key={m.label} aria-hidden transform={`translate(${String(x(m.value))},0)`}>
              <line
                y1={-4}
                y2={innerHeight}
                style={{ stroke: 'var(--chart-axis-label)' }}
                strokeDasharray="3 3"
                shapeRendering="crispEdges"
              />
              {showLabel && (
                <text
                  y={-(fontSize / 2 + 0.5)}
                  textAnchor="middle"
                  style={{ fill: 'var(--chart-axis-label)', fontSize }}
                >
                  {m.label}
                </text>
              )}
            </g>
          );
        })}
        {first && last && (
          <g aria-hidden className="tabular" style={{ fill: 'var(--chart-axis-label)', fontSize }}>
            <text y={innerHeight + fontSize + 5}>{formatX(first.from)}</text>
            <text x={innerWidth} y={innerHeight + fontSize + 5} textAnchor="end">
              {formatX(last.to)}
            </text>
          </g>
        )}
        {!print && (
          <rect
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onPointerMove={(e) => {
              const box = e.currentTarget.getBoundingClientRect();
              const i = Math.floor((e.clientX - box.left) / Math.max(1, barWidth));
              setHover(i >= 0 && i < data.length ? i : -1);
            }}
            onPointerLeave={() => {
              setHover(-1);
            }}
          />
        )}
      </g>
    </svg>
  );

  if (print) return svg;

  return (
    <div
      ref={ref}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onBlur={() => {
        setHover(-1);
      }}
      className="relative w-full rounded-md outline-none focus-visible:ring-2 focus-visible:ring-focus"
      style={{ height }}
    >
      {svg}
      {hovered && (
        <div
          role="tooltip"
          className="pointer-events-none absolute top-1 z-10 rounded-md bg-bg-2 px-2.5 py-1.5 text-xs shadow-popover"
          style={
            hover * barWidth > innerWidth * 0.6
              ? { right: width - (MARGIN.left + hover * barWidth) + 8 }
              : { left: MARGIN.left + (hover + 1) * barWidth + 8 }
          }
        >
          {formatBin(hovered.from, hovered.to, hovered.count / Math.max(1, values.length))}
        </div>
      )}
    </div>
  );
}
